import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api, inr, errorMessage, fileUrl } from "@/lib/api";
import { payWithRazorpay, loadRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { ShieldCheck, Truck, Wallet, CreditCard, Loader2, Lock } from "lucide-react";

export default function Checkout() {
  const { cart, clearCart, user, refreshCart } = useAuth();
  React.useEffect(() => { refreshCart(); }, [refreshCart]);
  const nav = useNavigate();
  const [address, setAddress] = React.useState({ full_name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const { data: opts } = useQuery({ queryKey: ["payment-options"], queryFn: () => api.get("/payment-options").then((r) => r.data), staleTime: 60 * 1000 });
  const [payment, setPayment] = React.useState("");
  const [coupon, setCoupon] = React.useState("");
  const [placing, setPlacing] = React.useState(false);
  const ordered = React.useRef(false);   // after an order is created we leave for the order page, not the empty bag

  // pick a sensible default once we know what's available
  React.useEffect(() => {
    if (!opts || payment) return;
    setPayment(opts.razorpay ? "razorpay" : opts.cod ? "cod" : "");
  }, [opts, payment]);
  React.useEffect(() => { if (opts?.razorpay) loadRazorpay(); }, [opts]);
  React.useEffect(() => { if (user?.name && !address.full_name) setAddress((a) => ({ ...a, full_name: user.name })); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const shipping = subtotal >= 3000 ? 0 : 150;
  const tax = subtotal * 0.05;
  const total = subtotal + tax + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login?redirect=/checkout"); return; }
    if (!payment) return toast.error("Please choose a payment method");
    if (!/^\d{6}$/.test(address.pincode.trim())) return toast.error("Please enter a 6-digit pincode");
    if (address.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid phone number");
    setPlacing(true);
    try {
      const items = cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity }));
      const r = await api.post("/checkout", { items, address, payment_method: payment, coupon_code: coupon.trim() || null });
      const order = r.data;
      ordered.current = true;
      if (payment === "razorpay") {
        const res = await payWithRazorpay(order.id, order.razorpay);
        clearCart();
        if (res.status === "paid") toast.success("Payment received — thank you! Your order is confirmed.");
        else if (res.status === "cancelled") toast("Payment not completed. You can pay any time from this order page.");
        else toast.error(res.message || "Payment failed. You can try again from this order page.");
      } else {
        clearCart();
        toast.success("Order placed! Pay when it's delivered.");
      }
      nav(`/account/orders/${order.id}`);
    } catch (err) {
      toast.error(errorMessage(err, "Checkout failed. Please try again."));
    } finally { setPlacing(false); }
  };

  React.useEffect(() => { if (cart.length === 0 && !placing && !ordered.current) nav("/cart"); }, [cart.length, placing, nav]);
  if (cart.length === 0) return null;

  const methods = [
    opts?.razorpay && { id: "razorpay", icon: CreditCard, title: "Pay online", text: "UPI, cards, net banking & wallets — secured by Razorpay" },
    opts?.cod && { id: "cod", icon: Wallet, title: "Cash on Delivery", text: "Pay in cash or UPI when your order arrives" },
  ].filter(Boolean);

  return (
    <form onSubmit={submit} className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[1fr_400px] gap-8 lg:gap-10">
      <div>
        <h1 className="h1-editorial mb-6">Checkout</h1>
        {!user && (
          <div className="mb-6 bg-[#FFF7E0] border border-[#E9D8A6] rounded-2xl p-4 text-sm">
            Please <Link to="/login?redirect=/checkout" className="font-semibold underline">sign in</Link> or <Link to="/register?redirect=/checkout" className="font-semibold underline">create an account</Link> to place your order.
          </div>
        )}
        <div className="space-y-6">
          <section className="bg-white border rounded-2xl p-5 sm:p-6">
            <h3 className="font-serif text-xl mb-4">Shipping address</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["full_name", "Full name", true, 2, "name"],
                ["phone", "Phone", true, 1, "tel"],
                ["pincode", "Pincode", true, 1, "postal-code"],
                ["line1", "House no., street, area", true, 2, "address-line1"],
                ["line2", "Landmark (optional)", false, 2, "address-line2"],
                ["city", "City", true, 1, "address-level2"],
                ["state", "State", true, 1, "address-level1"],
              ].map(([k, label, req, span, ac]) => (
                <input key={k} required={req} value={address[k]} onChange={(e) => setAddress({ ...address, [k]: e.target.value })} data-testid={`checkout-${k}`} placeholder={label} autoComplete={ac}
                       inputMode={k === "phone" ? "tel" : k === "pincode" ? "numeric" : undefined}
                       className={`border border-neutral-300 rounded-[10px] p-3 text-sm focus:outline-none focus:border-[#0E4D34] ${span === 2 ? "sm:col-span-2" : ""}`}/>
              ))}
            </div>
          </section>
          <section className="bg-white border rounded-2xl p-5 sm:p-6" data-testid="payment-methods">
            <h3 className="font-serif text-xl mb-4">Payment method</h3>
            {!opts && <div className="text-sm text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>Loading payment options…</div>}
            {opts && methods.length === 0 && <div className="text-sm text-[#991B1B]">Orders can't be paid online right now. Please contact us on WhatsApp to order.</div>}
            <div className="space-y-2">
              {methods.map((m) => (
                <label key={m.id} className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${payment === m.id ? "border-[#0E4D34] bg-[#E8F3EE]/50" : "hover:bg-neutral-50"}`}>
                  <input type="radio" name="payment" checked={payment === m.id} onChange={() => setPayment(m.id)} data-testid={`pay-${m.id}`} className="accent-[#0E4D34]"/>
                  <span className="w-10 h-10 rounded-full bg-white border flex items-center justify-center text-[#0E4D34] shrink-0"><m.icon size={18}/></span>
                  <div>
                    <div className="font-semibold text-sm">{m.title}</div>
                    <div className="text-xs text-neutral-500">{m.text}</div>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </div>
      </div>
      <aside className="bg-white border rounded-2xl p-5 sm:p-6 h-fit lg:sticky lg:top-28">
        <h3 className="font-serif text-xl mb-4">Order summary</h3>
        <div className="space-y-3 text-sm mb-4 max-h-72 overflow-y-auto">
          {cart.map((c) => (
            <div key={c.product_id} className="flex items-center gap-3">
              {c.image ? <img src={fileUrl(c.image)} alt="" className="w-12 h-14 object-cover rounded-md"/> : <span className="w-12 h-14 rounded-md bg-neutral-100"/>}
              <span className="flex-1 min-w-0"><span className="block truncate">{c.name}</span><span className="text-xs text-neutral-500">Qty {c.quantity}</span></span>
              <span className="text-right">{inr(c.price * c.quantity)}{Number(c.mrp) > Number(c.price) && <span className="block text-[11px] text-neutral-400 line-through">{inr(c.mrp * c.quantity)}</span>}</span>
            </div>
          ))}
        </div>
        <div className="border-t pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
          {cart.some(c => Number(c.mrp) > Number(c.price)) && <div className="flex justify-between text-[#991B1B]"><span>Discount included</span><span>−{inr(cart.reduce((t, c) => t + (Number(c.mrp) > Number(c.price) ? (c.mrp - c.price) * c.quantity : 0), 0))}</span></div>}
          <div className="flex justify-between"><span>GST (5%)</span><span>{inr(tax)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span>{shipping ? inr(shipping) : "Free"}</span></div>
          <div className="flex justify-between font-bold text-base pt-2 text-[#0E4D34]"><span>Total</span><span>{inr(total)}</span></div>
          {coupon.trim() && <div className="text-[11px] text-neutral-500">Coupon discount is applied when you place the order.</div>}
        </div>
        <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="checkout-coupon" className="w-full mt-4 border border-neutral-300 rounded-[10px] p-2.5 text-sm"/>
        <button type="submit" disabled={placing || !payment} data-testid="btn-place-order" className="btn-silk w-full mt-4 rounded-[10px] disabled:opacity-60">
          {placing ? <><Loader2 size={14} className="animate-spin mr-2"/>Please wait…</> : payment === "razorpay" ? <><Lock size={13} className="mr-2"/>Pay {inr(total)}</> : "Place order"}
        </button>
        <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-neutral-500">
          <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#0E4D34]"/>Secure payment</div>
          <div className="flex items-center gap-1.5"><Truck size={14} className="text-[#0E4D34]"/>Free shipping over ₹3,000</div>
        </div>
      </aside>
    </form>
  );
}
